<template>
  <div class="tech-demand">
    <!-- 页面标题 -->
    <achiTitle title="技术需求对接平台" subtitle="发布技术需求，寻求优质解决方案" />
    <!-- 筛选区域：靠左排列 -->
    <div class="filter-bar">
      <el-input v-model="searchQuery"
        placeholder="搜索需求名称..." prefix-icon="Search" clearable
        class="search-input"></el-input>
      <el-button size="large" @click="searchByName">搜索</el-button>

      <el-select
        v-model="selectedField"
        placeholder="选择技术领域"
        clearable
        class="filter-select"
        @change="handleSelectChange">
        <el-option v-for="field in techFields" :key="field" :label="field"
          :value="field"></el-option>
      </el-select>
    </div>
    <!-- 需求展示区域：每行单个卡片 -->
    <div class="demands-container">
      <div v-if="cardDatas.length === 0 && !loading" class="no-results">
        <el-empty description="没有找到符合条件的技术需求"></el-empty>
      </div>
      <!-- 加载中状态 -->
      <div v-if="loading" class="loading-container">
        <el-loading type="circle" text="加载中..."></el-loading>
      </div>

      <div class="demand-card" v-for="(demand, index) in cardDatas" :key="index">
        <div class="card-header">
          <h3 class="demand-title">{{ demand.demandName }}</h3>
          <div class="tag-group"> <!-- 修复标签布局：包裹所有tag -->
            <el-tag
              type="primary"
              v-for="(demandType, idx) in getDemandTypeList(demand.demandTypes)"
              :key="idx"
              class="type-tag">
              {{ demandType }}
            </el-tag>
          </div>
        </div>

        <div class="card-body" @click="goToDetail(demand)">
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">技术领域：</span>
              <span class="info-value">{{ demand.techField }}</span>
            </div>
            <div class="info-item demand-desc">
              <span class="info-label">需求描述：</span>
              <span class="info-value">{{ demand.demandDesc }}</span>
            </div>
          </div>
        </div>

        <div class="card-footer">
          <el-button type="primary" size="small" @click="goToDetail(demand)">查看详情</el-button>
        </div>
      </div>
    </div>

    <div class="pagination">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[6, 12, 18]"
        :page-size="pagination.pageSize"
        layout="total, prev, pager, next,sizes"
        :total="total"></el-pagination>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import achiTitle from '../components/achievement/achi_title.vue';
import { loadPageData } from '@/utils/MyUtils.js'
import demandApi from '@/api/demands.js'
export default {
  name: 'TechDemand',
  components: {
    achiTitle
  },
  data() {
    return {
      // 筛选表单数据（页面切换时会保留，除非刷新页面）
      searchQuery: '',
      selectedField: '', // 技术领域筛选
      selectedType: '', // 需求类型筛选（保留变量，便于后续扩展）
      // 分页配置
      pagination: {
        currentPage: 1,
        pageSize: 6,
        total: 0
      },

      // 数据相关
      cardDatas: [],
      loading: true,
      error: null
    };
  },
  computed: {
    // 核心优化：动态拼接筛选+分页参数，所有条件变化都会更新URL
    baseUrl() {
      const { currentPage, pageSize } = this.pagination;
      let url = `/techDemands?pageNum=${currentPage}&pageSize=${pageSize}`;
      
      // 拼接搜索参数（有值才拼，无值不拼）
      if (this.searchQuery.trim()) {
        url += `&demandName=${encodeURIComponent(this.searchQuery.trim())}`; // encodeURIComponent处理特殊字符
      }
      // 拼接技术领域筛选参数
      if (this.selectedField) {
        url += `&techField=${encodeURIComponent(this.selectedField)}`;
      }
      return url;
    },
    // 技术领域选项（从接口获取）
    techFields() {
      return demandApi.techFields || [];
    }
  },
  watch: {
    // 监听所有筛选/分页参数变化，自动加载数据（页面切换后参数不变也会触发？不，变化才触发）
    searchQuery: {
      handler() {
        this.pagination.currentPage = 1; // 搜索关键词变，重置到第一页
        this.loadData(this.baseUrl);
      },
      immediate: false // 初始不触发（created中已加载）
    },
    selectedField: {
      handler() {
        this.pagination.currentPage = 1; // 筛选条件变，重置到第一页
        this.loadData(this.baseUrl);
      },
      immediate: false
    },
    // 监听路由变化（关键！页面切换后重新加载数据，确保筛选状态生效）
    $route: {
      handler() {
        // 路由变化时（如从详情页返回），重新加载当前条件下的数据
        this.loadData(this.baseUrl);
      },
      immediate: false // 避免与created重复加载
    }
  },
  created() {
    // 组件创建时加载数据（初始加载，携带当前筛选/分页条件）
    this.loadData(this.baseUrl);
  },
  methods: {
    // 将字符串的需求转化为数组（优化：处理空值和空格）
    getDemandTypeList(demandTypes) {
      if (!demandTypes || typeof demandTypes !== 'string') return ['未知类型'];
      // 分割后去重、过滤空字符串
      return demandTypes.split(",").map(type => type.trim()).filter(type => type);
    },
    // 从接口加载数据（优化：统一处理筛选+分页）
    async loadData(url) {
      try {
        this.loading = true;
        let data = await loadPageData(this, url);
        this.total = data.total;
        this.cardDatas = data.records || [];
        this.error = null;
      } catch (err) {
        this.error = '数据加载失败，请刷新页面重试';
        this.cardDatas = [];
        console.error('加载数据出错:', err);
      } finally {
        this.loading = false;
      }
    },
    // 跳转到需求详情界面
    goToDetail(demand) {
      this.$router.push({
        name: 'TechDemandDetail',
        params: { id: demand.id }
      });
    },
    // 每页条数变化
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 当前页码变化
    handleCurrentChange(page) {
      this.pagination.currentPage = page;
      this.loadData(this.baseUrl);
    },
    // 搜索按钮点击（优化：与watch逻辑一致，避免重复代码）
    searchByName() {
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 按技术领域筛选（优化：与watch逻辑一致）
    searchByField(field) {
      this.selectedField = field; // 直接赋值，watch会触发加载
    },
    // 按需求类型筛选（保留方法，便于后续扩展）
    searchByType(type) {
      this.selectedType = type;
      this.pagination.currentPage = 1;
      this.loadData(this.baseUrl);
    },
    // 技术领域选择变化（简化：直接赋值给selectedField，watch自动触发）
    handleSelectChange(value) {
      this.selectedField = value;
    },
    // 需求类型选择变化（保留方法，便于后续扩展）
    handleTypeChange(value) {
      this.selectedType = value;
    }
  }
};
</script>

<style scoped>
.tech-demand {
  max-width: 85%;
  margin: 0 auto;
  padding: 20px;
}

/* 筛选区域：靠左排列，保持横向布局 */
.filter-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
}

.filter-select {
  width: 200px;
}

.search-input {
  width: 300px;
  min-width: 180px;
}

/* 需求展示区域：每行单个卡片，纵向排列 */
.demands-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 30px;
  position: relative;
}

/* 加载中样式 */
.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

/* 横向拉长卡片样式 */
.demand-card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.demand-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

/* 卡片头部：标题+标签横向分布（修复标签布局） */
.card-header {
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  gap: 16px;
}

.demand-title {
  font-size: 18px;
  font-weight: 500;
  color: #1f2d3d;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0; /* 修复flex子元素最小宽度问题 */
}

/* 标签组：包裹所有tag，确保靠右紧凑排列 */
.tag-group {
  display: flex;
  gap: 8px;
  align-items: center;
  white-space: nowrap;
}

.type-tag {
  white-space: nowrap;
  padding: 4px 10px;
  font-size: 12px;
}

/* 卡片主体：横向布局，多字段并排 */
.card-body {
  padding: 20px 24px;
  cursor: pointer;
}

.info-row {
  display: flex;
  gap: 40px;
  flex-wrap: wrap;
  align-items: center;
}

.info-item {
  font-size: 14px;
  line-height: 1.8;
  display: flex;
  align-items: center;
}

.info-label {
  color: #86909c;
  display: inline-block;
  width: 100px;
  text-align: right;
  margin-right: 8px;
  font-weight: 500;
}

.info-value {
  color: #1f2d3d;
  vertical-align: middle;
}

/* 需求描述：限制两行显示，保持原有样式 */
.demand-desc {
  flex: 1;
  min-width: 300px;
}

.demand-desc .info-value {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.6;
  min-height: 3.2em;
}

/* 卡片底部：按钮靠右 */
.card-footer {
  padding: 12px 24px;
  border-top: 1px solid #f0f0f0;
  text-align: right;
  background-color: #fafafa;
}

/* 分页区域：居中显示 */
.pagination {
  text-align: center;
  margin-top: 30px;
}

.no-results {
  text-align: center;
  padding: 60px 0;
}

/* 响应式调整：小屏幕适配 */
@media (max-width: 768px) {
  .tech-demand {
    max-width: 95%;
    padding: 15px;
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-select,
  .search-input {
    width: 100%;
  }

  .info-row {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .info-label {
    text-align: left;
    width: auto;
    margin-right: 12px;
  }

  .demand-desc {
    min-width: 100%;
  }

  .tag-group {
    flex-wrap: wrap;
    gap: 6px;
  }
}

/* 中等屏幕适配 */
@media (min-width: 769px) and (max-width: 1200px) {
  .info-row {
    gap: 20px;
  }

  .demand-title {
    max-width: 60%;
  }
}
</style>